import { Icon } from '@iconify/react';
import { Menu as AntdMenu, Tooltip, ConfigProvider } from 'antd';
import DarkMaximorLogoCollapsed from 'assets/brand/dark_logo_collapsed.svg';
import MaximorLogoCollapsed from 'assets/brand/dark_logo_collapsed.svg';
import MaximorLogoExpanded from 'assets/brand/dark_logo_expanded.svg';
import { FC, MutableRefObject, useMemo } from 'react';
import type React from 'react';
import { useTheme } from 'theme/MaximorThemeProvider';
import { colors } from 'theme/colors';

import { CloseProgressBox } from 'pages/period-close/demo/components/CloseProgressBox';

import { Button } from 'components/general/Button';

import { type MenuItem } from 'hooks/utils/useMenu';

import { featureFlags } from 'utils/config/featureFlags';

import { Workspace } from 'store/workspace.store';

import { WorkSpaceSelector } from 'layout/WorkSpaceSelector';

export interface MenuInfo {
  item: React.ReactInstance;
  key: string;
  keyPath: string[];
  domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;
}

interface NavigationProps {
  isSidenavCollapsed: boolean;
  toggleCollapsed: () => void;
  workspace: Workspace | null;
  workspaces: Workspace[];
  setWorkspace: (workspace: Workspace) => void;
  links: MenuItem[];
  defaultOpen: string[];
  setDefaultOpen: (keys: string[]) => void;
  defaultSelected: string[];
  menuStyle: any;
  handleMenuClick: (item: MenuInfo) => void;
  styles: {
    menuGreen: string;
    tooltipStyles: string;
    popupSubmenuStyles: string;
  };
  setShowChatBot: (show: boolean) => void;
  // Enhanced props for user collapse functionality
  userCollapsedMenus: Set<string>;
  setUserCollapsedMenus: (menus: Set<string>) => void;
  isSystemUpdateRef: MutableRefObject<boolean>;
}

export const maxiBtnStyle = {
  backgroundColor: colors.lime5,
  borderColor: colors.lime6,
  color: colors.lime10,
};

const Navigation: FC<NavigationProps> = ({
  isSidenavCollapsed,
  toggleCollapsed,
  workspace,
  workspaces,
  setWorkspace,
  links,
  defaultOpen,
  setDefaultOpen,
  defaultSelected,
  menuStyle,
  handleMenuClick,
  styles,
  setShowChatBot,
  userCollapsedMenus,
  setUserCollapsedMenus,
  isSystemUpdateRef,
}) => {
  const { theme: appTheme } = useTheme();
  const { FF__ASK_MAXI_AI_BUTTON, FF__CLOSE_MONTH_CLOSE } = featureFlags;

  const navigationItems = useMemo(() => {
    return links.map((link) => {
      return {
        key: link.permissionKey,
        label: link.label,
        path: link.path,
        icon: link.icon,
        children: link.children?.map((child) => ({
          key: child.permissionKey,
          label: child.label,
          path: child.path,
        })),
      };
    });
  }, [links]);

  // Enhanced onOpenChange handler with user preference tracking
  const handleOpenChange = (keys: string[]) => {
    if (isSidenavCollapsed) {
      setDefaultOpen(keys);
      return;
    }

    // Determine if this is a user-initiated update
    const isUserInitiatedUpdate = !isSystemUpdateRef.current;

    const newUserCollapsed = new Set(userCollapsedMenus);

    // Only mark items as user-collapsed if they were previously open
    // and are now explicitly closed by user action
    if (isUserInitiatedUpdate) {
      defaultOpen.forEach((openKey) => {
        if (!keys.includes(openKey)) {
          newUserCollapsed.add(openKey);
        }
      });

      // Track what user intentionally expanded
      keys.forEach((key: string) => {
        if (newUserCollapsed.has(key)) {
          newUserCollapsed.delete(key);
        }
      });

      setUserCollapsedMenus(newUserCollapsed);
    }

    // Always show the menu opened for the selected item (2 max menus opened at once)
    if (!keys || keys.length === 0) {
      setDefaultOpen([]);
      return;
    }

    const newOpenKeys = [keys[0], keys[keys.length - 1]];
    setDefaultOpen(newOpenKeys);
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Menu: {
            colorBgContainer: colors.primaryGreen7,
            itemSelectedBg: colors.primaryGreen4,
            itemActiveBg: colors.colorPrimaryBorder,
            itemHoverBg: colors.primaryGreen4,
            itemSelectedColor: colors.colorWhite,
            colorText: colors.colorTextLightSolid,
            itemColor: colors.colorTextLightSolid,
            itemHoverColor: colors.colorTextLightSolid,
            colorPrimary: colors.colorTextLightSolid,
            popupBg: colors.primaryGreen7,
          },
          Select: {
            colorBgContainer: colors.primaryGreen7,
            colorBorder: colors.primaryGreen6,
            colorText: colors.colorTextLightSolid,
            colorPrimaryBg: colors.primaryGreen7,
            colorBgElevated: colors.primaryGreen7,
            colorPrimary: colors.colorTextLightSolid,
            colorTextPlaceholder: colors.colorTextLightSolid,
            controlOutline: colors.primaryGreen6,
            colorPrimaryHover: colors.primaryGreen6,
            activeBorderColor: colors.primaryGreen6,
            controlOutlineWidth: 1,
            optionSelectedBg: colors.colorPrimaryBorderHover,
            colorTextLabel: colors.colorTextLightSolid,
            colorPrimaryTextHover: colors.colorTextLightSolid,
            optionActiveBg: 'transparent',
            colorIcon: colors.colorTextLightSolid,
            colorIconHover: colors.colorTextLightSolid,
          },
        },
      }}
    >
      <div
        className="flex h-full flex-col"
        data-testid="sidebar-container"
        style={{
          backgroundColor: colors.primaryGreen7,
          width: !isSidenavCollapsed ? '240px' : '80px',
        }}
      >
        <div
          data-testid="sidebar-expanded"
          onClick={toggleCollapsed}
          className={`cursor-pointer flex items-center justify-center rounded-md transition-all duration-300 ${
            !isSidenavCollapsed ? 'mx-4 my-5' : ' m-2 mb-0'
          }`}
        >
          {isSidenavCollapsed ? (
            <Tooltip
              placement="right"
              title={<div className="p-1">Expand navigation</div>}
            >
              <img
                src={
                  appTheme === 'dark'
                    ? DarkMaximorLogoCollapsed
                    : MaximorLogoCollapsed
                }
                className="h-5 w-6 object-contain mt-4"
              />
            </Tooltip>
          ) : (
            <div className="flex items-center justify-between w-full">
              <img
                src={MaximorLogoExpanded}
                className={`w-full h-6 max-w-[118px] object-contain transition-all duration-300`}
              />
              <Tooltip
                placement="right"
                title={<div className="p-1">Collapse navigation</div>}
              >
                <Button
                  type="text"
                  className="h-6 w-6 max-w-6 text-white hover:text-white! bg-transparent hover:bg-transparent!"
                  icon={
                    <Icon
                      icon="octicon:sidebar-expand-24"
                      className="h-[14px] w-[14px] text-white"
                    />
                  }
                />
              </Tooltip>
            </div>
          )}
        </div>
        {/* </Tooltip> */}

        {!isSidenavCollapsed && (
          <div className="px-4 pb-3 pt-2">
            <WorkSpaceSelector
              options={workspaces}
              value={workspace}
              onChange={(newWorkspace) => setWorkspace(newWorkspace)}
            />
          </div>
        )}

        <div className="flex-1 overflow-y-auto mb-4">
          <AntdMenu
            openKeys={defaultOpen}
            selectedKeys={defaultSelected}
            mode="inline"
            inlineCollapsed={isSidenavCollapsed}
            items={navigationItems}
            onClick={handleMenuClick}
            onOpenChange={handleOpenChange}
            style={menuStyle}
            className={`${styles.menuGreen} ${styles.tooltipStyles} ${styles.popupSubmenuStyles}`}
            triggerSubMenuAction="click"
          />
        </div>

        {FF__CLOSE_MONTH_CLOSE && !isSidenavCollapsed && (
          <div className="px-4 pb-2">
            <CloseProgressBox />
          </div>
        )}

        <div className="p-4 flex flex-col items-center gap-4">
          <Tooltip title="support@maximor.ai">
            <a
              href="mailto:support@maximor.ai"
              className={`w-full flex items-center ${
                isSidenavCollapsed ? 'justify-center' : 'justify-start'
              } gap-2 text-white no-underline text-base`}
              aria-label="Email support at support@maximor.ai"
            >
              <Icon
                icon="ant-design:mail-outlined"
                className={isSidenavCollapsed ? 'w-4 h-4' : 'w-4 h-4'}
              />
              {!isSidenavCollapsed && <span>support@maximor.ai</span>}
            </a>
          </Tooltip>
          {FF__ASK_MAXI_AI_BUTTON && (
            <Button
              style={maxiBtnStyle}
              icon={<Icon icon="ix:ai" className={`w-4 h-4`} />}
              onClick={() => setShowChatBot(true)}
              className="w-full"
              aria-label="Ask Maxi AI"
            >
              {isSidenavCollapsed ? '' : 'Ask Maxi AI'}
            </Button>
          )}
        </div>
      </div>
    </ConfigProvider>
  );
};

export { Navigation };
